import { useEffect, useRef, useState } from "react";

import Text from "../Text/Text";
import {
  Container,
  ContentWrapper,
  ErrorWrapper,
  IconWrapper,
  OptionWrapper,
  OptionsWrapper,
  SelectedWrapper,
  TextWrapper,
} from "./styled";
import { OptionValue, SingleSelectProps } from "./types";
import Input from "../Input/Input";
import { useTheme } from "styled-components";
import useOutsideClick from "../../hooks/useOutsideClick";
import { FontSize } from "../../utils/Fonts";

const LABEL_DEFAULT = "Label";

const MINIMUN_OPTIONS = 0;

const DEFAUL_OPTION: OptionValue = { label: "Sin seleccionar", value: null };

export default function SingleSelect(singleSelectProps: SingleSelectProps) {
  const {
    options,
    withSearch,
    width,
    selected,
    label,
    isRequired,
    hasError,
    messageError,
    handleSelected,
    withBorder,
    defaultValue,
  } = singleSelectProps;

  const [isOpen, setIsOpen] = useState<boolean>(false);

  const [searchText, setSearchText] = useState<string | null>(null);

  const [currentSelected, setCurrentSelected] = useState<OptionValue>(
    defaultValue || DEFAUL_OPTION
  );

  const [internalOptions, setInternalOptions] = useState<OptionValue[]>();

  const contentRef = useRef<HTMLDivElement | null>(null);

  const buttonRefElement = useRef<HTMLDivElement | null>(null);

  const border = withBorder ?? true;

  const handleOpen = () => {
    setIsOpen((previewValue) => !previewValue);
  };

  const handleSelect = (optionValue: OptionValue) => {
    if (optionValue === currentSelected) {
      setCurrentSelected(DEFAUL_OPTION);
      handleClose();
      return;
    }
    setCurrentSelected(optionValue);
    handleClose();
  };

  useEffect(() => {
    if (selected) return;

    setCurrentSelected(DEFAUL_OPTION);
  }, [selected]);

  const handleClose = () => {
    if (!isOpen) return;
    setSearchText("");
    setIsOpen((previewValue) => !previewValue);
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
  };

  const withError = hasError && messageError;

  useEffect(() => {
    setInternalOptions(options);
  }, [options]);

  useEffect(() => {
    if (!handleSelected) return;

    if (!currentSelected.value) {
      handleSelected(DEFAUL_OPTION.value);
      return;
    }

    handleSelected(currentSelected.value);
  }, [currentSelected]);

  useOutsideClick({
    handleOutside: handleClose,
    refElement: contentRef,
    ignoreRefElement: buttonRefElement,
  });

  useEffect(() => {
    if (!searchText) {
      setInternalOptions(options);
    }
    const filtered = options.filter((option) => {
      if (!searchText || searchText === "") {
        return option;
      }
      return option.label.toLowerCase().includes(searchText.toLowerCase());
    });
    setInternalOptions(filtered);
  }, [searchText, options]);

  const formmatedLabel = (): string => {
    if (!label) return LABEL_DEFAULT;

    if (!isRequired) return label;

    return `${label} *`;
  };

  return (
    <Container width={width}>
      <SelectedWrapper
        $withBorder={border}
        ref={buttonRefElement}
        onClick={handleOpen}
      >
        {label && (
          <TextWrapper>
            <Text
              value={formmatedLabel()}
              fontSize={FontSize.TEXT_XS}
              lineCount={1}
            />
          </TextWrapper>
        )}
        <Text
          value={currentSelected.label}
          fontSize={FontSize.TEXT_SM}
          lineCount={1}
        />
        <IconWrapper $isOpen={isOpen}></IconWrapper>
      </SelectedWrapper>
      <ContentWrapper ref={contentRef} $isVisible={isOpen}>
        {withSearch && (
          <Input
            value={searchText ?? ""}
            placeholder="Buscar"
            width="100%"
            height="2.5rem"
            handleChangeInput={handleSearch}
          />
        )}
        {internalOptions && internalOptions.length > MINIMUN_OPTIONS && (
          <OptionsWrapper>
            {internalOptions.map((option) => (
              <OptionWrapper
                $isSelected={option === currentSelected}
                key={option.value}
                onClick={() => handleSelect(option)}
              >
                <Text
                  value={option.label}
                  fontSize={FontSize.TEXT_SM}
                  lineCount={2}
                />
              </OptionWrapper>
            ))}
          </OptionsWrapper>
        )}
        {!internalOptions ||
          (internalOptions.length === MINIMUN_OPTIONS && (
            <Text
              value={"Sin opciones disponibles"}
              fontSize={FontSize.TEXT_SM}
            />
          ))}
      </ContentWrapper>
      {withError && (
        <ErrorWrapper>
          <Text
            value={messageError}
            fontSize={FontSize.TEXT_XS}
            fontColor={"#E62231"}
          />
        </ErrorWrapper>
      )}
    </Container>
  );
}
